import tileUrl from "@/assets/brand/tranche/icon_tile.svg";
import symbolUrl from "@/assets/brand/tranche/symbol.svg";
import symbolDarkUrl from "@/assets/brand/tranche/symbol_dark.svg";
import symbolWhiteUrl from "@/assets/brand/tranche/symbol_white.svg";
import { CROSSFADE, SPRINGS } from "@/lib/spring";
import { useReducedMotion } from "motion/react";
import * as m from "motion/react-m";

/**
 * 브랜드 마크. SVG 를 `<img>` 로 참조해요 — 인라인 SVG 는 svgr 의존성이 필요하고
 * 컴포넌트 파일에 브랜드 hex 를 끌고 들어와요(디자인 시스템 어드히런스 린트가 막는 그것).
 * 색 변형은 파일을 나눠서 처리합니다(`symbol.svg` / `symbol_white.svg`).
 *
 * `width`/`height` 를 항상 박습니다. 없으면 로드 중에 레이아웃이 튀어요.
 */
type SymbolVariant = "default" | "white" | "tile";

const SRC: Record<SymbolVariant, string> = {
  default: symbolUrl,
  white: symbolWhiteUrl,
  tile: tileUrl,
};

export function BrandSymbol({
  size = 28,
  variant = "default",
  className,
}: {
  size?: number;
  variant?: SymbolVariant;
  className?: string;
}) {
  const img = (
    <img
      src={SRC[variant]}
      alt=""
      // 장식이에요. 옆에 워드마크 텍스트가 서 있어서 스크린리더에 이름이 두 번 읽히면 안 돼요.
      aria-hidden
      width={size}
      height={size}
      className={className}
    />
  );
  if (variant !== "default") return img;
  /*
    다크에서는 다크 변형으로 갈아끼워요. `symbol.svg` 의 가로획은 잉크(#171717)라 다크 크롬
    위에서 바탕에 묻히고, 헤더에는 블루 세로획 하나만 떠 있었어요(QA 다크 스크린샷 실측).
    `<img>` 안쪽에는 CSS 가 닿지 않으므로 파일을 바꾸는 수밖에 없고, 테마는 시스템 설정을
    따르니(`styles/apple/tokens.css`) 같은 미디어쿼리를 `<source>` 에 겁니다. 스크립트가
    없어서 nginx CSP 와도 무관해요.

    `symbol_white.svg` 가 아니라 `symbol_dark.svg` 예요 — 흰 변형은 세로획까지 희게 칠해서
    "지금 집행 중인 조각" 인 블루가 사라집니다.
  */
  return (
    <picture className="contents">
      <source srcSet={symbolDarkUrl} media="(prefers-color-scheme: dark)" />
      {img}
    </picture>
  );
}

/**
 * 가로 락업 — 심볼 + 워드마크. 헤더·로그인 자리.
 *
 * 워드마크는 텍스트로 조판해요. 글자 일곱 개 때문에 웹폰트를 추가로 받지 않습니다
 * (`docs/brand/tranche-brand-board.html` 의 Wordmark 항목).
 */
export function BrandLockup({
  size = 24,
  variant = "default",
  className,
}: {
  size?: number;
  variant?: "default" | "white";
  className?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-2 ${className ?? ""}`}>
      <BrandSymbol size={size} variant={variant} />
      <span className="wds-headline-2 tracking-tight">tranche</span>
    </span>
  );
}

/*
  큰 자리(랜딩·로그인·404)에서 쓰는 **조각으로 조립한** 마크.

  `<img>` 는 내부에 CSS 가 닿지 않아서 조각을 따로 움직일 수 없어요. 그래서 큰 자리에서만
  같은 기하(`symbol.svg` 의 viewBox 240)를 토큰 색 블록으로 다시 세웁니다 — hex 를 끌고
  오지 않고(`bg-primary` = 브랜드 블루, `bg-label-strong` = 잉크), 다크 모드에서는 잉크가
  저절로 흰색으로 뒤집혀요(`symbol_white.svg` 를 따로 배선하지 않아도 됩니다).

  쌓는 순서는 SVG 와 같아요 — 세로획을 먼저 깔고 가로획을 위에 얹습니다(`BRAND.md`
  렌더 확인 1번).

  작은 자리(헤더 24px·파비콘)는 계속 `BrandSymbol`(`<img>`)이에요. 16~32px 에서는 슬릿이
  1px 아래로 수렴해야 하는데(`BRAND.md` 3번) 블록 조립은 그 열화를 재현하지 않습니다.
*/
const VIEW = 240;
const STEM = { x: 102, y: 38, w: 36, h: 164 };
const BARS = [
  { x: 20, w: 63 },
  { x: 89, w: 62 },
  { x: 157, w: 63 },
] as const;
const BAR_Y = 38;
const BAR_H = 36;
const RX = 6;

function pct(value: number): string {
  return `${(value / VIEW) * 100}%`;
}

/**
 * 조각 마크. 처음 그려질 때 **잘리고 꽂혀요**(`DESIGN.md` 모션 동사) — 블루 세로획이 아래에서
 * 올라와 꽂히고, 가로획 세 조각이 한 축으로 미끄러져 슬릿을 남기고 앉습니다. 튐은 없어요.
 * reduced-motion 이면 미끄러짐 없이 불투명도만 교차합니다.
 *
 * `missing` 을 주면 그 조각 자리를 비우고 점선 윤곽만 남겨요 — 404 의 "빠진 조각".
 */
export function BrandGlyph({
  size = 56,
  missing,
  className,
}: {
  size?: number;
  missing?: 0 | 1 | 2;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const radius = (RX / VIEW) * size;
  return (
    <span
      aria-hidden="true"
      className={`relative inline-block shrink-0 ${className ?? ""}`}
      style={{ width: size, height: size }}
    >
      <m.span
        className="absolute bg-primary"
        style={{
          left: pct(STEM.x),
          top: pct(STEM.y),
          width: pct(STEM.w),
          height: pct(STEM.h),
          borderRadius: radius,
          transformOrigin: "50% 100%",
        }}
        initial={reduce ? { opacity: 0 } : { opacity: 0, y: size * 0.12, scaleY: 0.4 }}
        animate={{ opacity: 1, y: 0, scaleY: 1 }}
        transition={reduce ? CROSSFADE : SPRINGS.move}
      />
      {BARS.map((bar, index) => {
        const style = {
          left: pct(bar.x),
          top: pct(BAR_Y),
          width: pct(bar.w),
          height: pct(BAR_H),
          borderRadius: radius,
        };
        if (index === missing) {
          return (
            <span
              key={bar.x}
              className="absolute border border-dashed border-line-normal"
              style={style}
            />
          );
        }
        return (
          <m.span
            key={bar.x}
            className="absolute bg-label-strong"
            style={style}
            initial={reduce ? { opacity: 0 } : { opacity: 0, x: -size * 0.18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={reduce ? CROSSFADE : { ...SPRINGS.ui, delay: 0.12 + index * 0.05 }}
          />
        );
      })}
    </span>
  );
}
